import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Button } from "./ui";
import { api } from "../services";
import { useTranslation } from "react-i18next";
import FilterDropdown from "./dropdown/FilterDropdown";
import { Printer, Maximize2, Minimize2, Menu } from "lucide-react";
import PhoneOutlinedIcon from "./order/PhoneOutlinedIcon";
import { formatDistanceStrict, differenceInSeconds, addMinutes } from "date-fns";
import { ar } from "date-fns/locale/ar";
import "./OrdersDisplay.css";
import { MDBCol, MDBRow } from "mdb-react-ui-kit";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CustomDropdown from "./dropdown/CustomDropdown";
import DeleteModal from "./Modals/DeleteModal";
import { useOrderNotification } from "../contexts/OrderNotificationContext";
import { useOrdersPageSession } from "../hooks/useOrdersPageSession";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logos/logo.png";
import GlobalOrderNotification from "./GlobalOrderNotification";
import { isDashboardFulfillmentLocked, fulfillmentLockTooltipKey } from "../utils/posReadOnly";
import DisabledControlTooltipWrap from "./DisabledControlTooltipWrap";
import { orderAgentLogoEntries } from "../utils/platformLogos";
import { formatPlatformName } from "../utils/utils";
import AddressReveal from "./order/AddressReveal";
import {
  getOrderContactPhone,
  getOrderAddressDisplay,
} from "../utils/orderCustomerDisplay";
import "./order/orderCustomerDisplay.css";

// Prefer platform external ID; for DC/Order Agent show readable order_id in
// primary UI — External ID is rendered separately where needed.
const getExternalOrderId = (order) => {
  if (!order) return '';
  const platform = String(order.platform || '').toUpperCase();
  const isAgentFamily =
    order.is_chat_agent_order ||
    platform === 'DIGITAL_CATALOG' ||
    platform === 'CHAT_AGENT';
  if (isAgentFamily) {
    return (
      order.order_id ||
      order.chat_agent_external_id ||
      order.jahez_id ||
      order.external_id ||
      order._id ||
      ''
    );
  }
  return (
    order.jahez_id ||
    order.chat_agent_external_id ||
    order.external_id ||
    order.order_id ||
    order._id ||
    ''
  );
};

// Helper function to check if order has expired
const isOrderExpired = (createdAt, platform) => {
  if (!createdAt) return false;

  const platformUpper = platform?.toUpperCase();
  let timeLimitMinutes = 5;
  
  // Jahez = 5 minutes
  if (platformUpper === "JAHEZ") {
    timeLimitMinutes = 5;
  }
  // Keeta = 5 minutes
  else if (platformUpper === "KEETA") {
    timeLimitMinutes = 5;
  }
  // The Chefz = 10 minutes
  else if (platformUpper === "THE CHEFZ" || platformUpper === "CHEFZ") {
    timeLimitMinutes = 10;
  }
  // Ninja = 30 minutes
  else if (platformUpper === "NINJA") {
    timeLimitMinutes = 30;
  }
  // Chat Agent = 15 minutes
  else if (platformUpper === "CHAT_AGENT" || platformUpper === "DIGITAL_CATALOG") {
    timeLimitMinutes = 15;
  }

  const createdAtDate = new Date(createdAt);
  const deadline = addMinutes(createdAtDate, timeLimitMinutes);
  const now = new Date();
  const diffInSeconds = differenceInSeconds(deadline, now);

  return diffInSeconds <= 0;
};

const OrderTimer = ({ createdAt, platform, status, userGetMode }) => {
  const { t } = useTranslation("translation");
  const [timeLeft, setTimeLeft] = useState("");
  const [isExpired, setIsExpired] = useState(false);

  const orderTypeLabel =
    String(userGetMode || "").toLowerCase().trim() === "pickup"
      ? t("pickup", "Pickup")
      : t("delivery", "Delivery");

  const getTimeLimit = useCallback((platform) => {
    const platformUpper = platform?.toUpperCase();
    
    // Jahez = 5 minutes
    if (platformUpper === "JAHEZ") {
      return 5;
    }
    // Keeta = 5 minutes
    else if (platformUpper === "KEETA") {
      return 5;
    }
    // The Chefz = 10 minutes
    else if (platformUpper === "THE CHEFZ" || platformUpper === "CHEFZ") {
      return 10;
    }
    // Ninja = 30 minutes
    else if (platformUpper === "NINJA") {
      return 30;
    }
    // Order Agent (Chat Agent) = 15 minutes
    else if (platformUpper === "CHAT AGENT" || platformUpper === "ORDER AGENT" || platformUpper === "AI ORDER AGENT" || platformUpper === "CHAT_AGENT" || platformUpper === "DIGITAL_CATALOG" || platformUpper === "DIGITAL CATALOG") {
      return 15;
    }
    
    return 5;
  }, []);

  useEffect(() => {
    // Only run timer for PENDING orders
    if (!createdAt || status !== "PENDING") return;

    const timeLimitMinutes = getTimeLimit(platform);
    const createdAtDate = new Date(createdAt);
    const deadline = addMinutes(createdAtDate, timeLimitMinutes);

    const updateTimer = () => {
      const now = new Date();
      const diffInSeconds = differenceInSeconds(deadline, now);

      if (diffInSeconds <= 0) {
        setTimeLeft("00:00");
        setIsExpired(true);
        return;
      }

      const minutes = Math.floor(diffInSeconds / 60);
      const seconds = diffInSeconds % 60;
      setTimeLeft(
        `${minutes.toString().padStart(2, "0")}:${seconds
          .toString()
          .padStart(2, "0")}`
      );
      setIsExpired(false);
    };

    updateTimer();
    const timerId = setInterval(updateTimer, 1000);

    return () => clearInterval(timerId);
  }, [createdAt, platform, status, getTimeLimit]);

  // Non-PENDING: show fulfilment type only (Figma ODS)
  if (status !== "PENDING") {
    return (
      <div className="d-flex justify-content-between" style={{ width: "100%" }}>
        <p className="fw-bold m-0 text-black">{orderTypeLabel}</p>
      </div>
    );
  }

  // For PENDING orders that expired
  if (isExpired) {
    return (
      <div className="d-flex justify-content-between" style={{ width: "100%" }}>
        <p className="fw-bold m-0 text-black">{orderTypeLabel}</p>
        <p className="fw-bold m-0 text-danger">{t("Expired")}</p>
      </div>
    );
  }

  // For PENDING orders with active timer — type left, countdown right
  return (
    <div className="d-flex justify-content-between" style={{ width: "100%" }}>
      <p className="fw-bold m-0 text-black">{orderTypeLabel}</p>
      <p className="fw-bolder m-0">
        <span className="fw-bold text-black">{timeLeft}</span>
      </p>
    </div>
  );
};

const platformLogos = {
  JAHEZ: {
    src: "/images/jahez-new-logo.svg",
    alt: "Jahez",
    height: "50px",
  },
  KEETA: {
    src: "/images/keeta.svg",
    alt: "Keeta",
    height: "52px",
  },
  "THE CHEFZ": {
    src: "/images/the_chefz_logo.svg",
    alt: "The Chefz",
    height: "48px",
  },
  NINJA: {
    src: "/images/ninga.svg",
    alt: "Ninja",
    height: "52px",
  },
  ...orderAgentLogoEntries("50px"),
  DIGITAL_CATALOG: {
    src: "/images/loops.svg",
    alt: "Digital Catalog",
    height: "50px",
  },
  "DIGITAL CATALOG": {
    src: "/images/loops.svg",
    alt: "Digital Catalog",
    height: "50px",
  },
};

const OrdersDisplay = () => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const navigate = useNavigate();
  const {
    orderRefreshTrigger,
    showNotification,
    newOrder,
    handleViewOrder,
    handleDismiss,
    soundEnabled,
    toggleSound,
  } = useOrderNotification();
  const [orders, setOrders] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false); // New state for filter/tab change loading
  const [hasMore, setHasMore] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [orderStats, setOrderStats] = useState({});
  const [printerConfigs, setPrinterConfigs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(() => {
    try {
      const saved = localStorage.getItem('persistentBranchFilter');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error loading branch filter from localStorage:', error);
      return null;
    }
  });
  const [storeId, setStoreId] = useState(null);
  const [rawPlatforms, setRawPlatforms] = useState([]);

  useEffect(() => {
    if (!branches || branches.length === 0) return;
    const updated = branches.map((branch) => {
      const localizedName = isArabic
        ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
        : branch?.name?.en || branch?.name;
      const storeDisplay = branch.storeName || "";
      return {
        ...branch,
        displayName: storeDisplay
          ? `${localizedName} - ${storeDisplay}`
          : localizedName,
      };
    });
    setBranches(updated);
  }, [isArabic]);

  // Save branch selection to localStorage
  const saveBranchToStorage = (branch) => {
    try {
      if (branch) {
        localStorage.setItem('persistentBranchFilter', JSON.stringify(branch));
      } else {
        localStorage.removeItem('persistentBranchFilter');
      }
    } catch (error) {
      console.error('Error saving branch filter to localStorage:', error);
    }
  };

  const [stores, setStores] = useState([]);
  const [updatingOrders, setUpdatingOrders] = useState(new Set()); // Track which orders are being updated
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [isProcessingReject, setIsProcessingReject] = useState(false);
  const observerRef = useRef();
  const initialFetchDoneRef = useRef(false);
  const audioRef = useRef(null);
  const hasInteractedRef = useRef(false);
  const fetchingRef = useRef(false);
  const currentFilterRef = useRef("all");
  const [datePreset, setDatePreset] = useState("today");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("All");
  const datePresetOptions = useMemo(() => {
    const base = [
      { id: "today", name: t("datePresets.today", "Today") },
      { id: "yesterday", name: t("datePresets.yesterday", "Yesterday") },
      { id: "last2", name: t("datePresets.last2", "Last 2 Days") },
      { id: "last7", name: t("datePresets.last7", "Last 7 Days") },
      { id: "last30", name: t("datePresets.last30", "Last 30 Days") },
    ];
    const hasAnyDate = Boolean(dateFrom) || Boolean(dateTo);
    const fmtDisp = (s) => {
      if (!s) return "";
      const [y, m, d] = s.split("-");
      return `${m}-${d}-${y}`;
    };
    const arrow = isArabic ? "\u2190" : "\u2192";
    const left = fmtDisp(dateFrom);
    const right = fmtDisp(dateTo);
    const rangeLabel = left && right ? `${left} ${arrow} ${right}` : (left || right);
    const customLabel = hasAnyDate ? `${rangeLabel}` : "";
    return hasAnyDate ? [...base, { id: "customRange", name: customLabel }] : base;
  }, [t, dateFrom, dateTo, isArabic]);

  const platformOptions = useMemo(() => {
    const options = [{ id: "All", name: t("platforms.all", "All") }];
    if (rawPlatforms.length > 0) {
      rawPlatforms.forEach((p) => {
        options.push({
          id: p.key,
          name: isArabic ? p.nameAr : p.nameEn,
        });
      });
    }
    return options;
  }, [rawPlatforms, isArabic, t]);

  // Resolve store name from order.store by matching against stores[].admin
  const getStoreNameFromOrder = useCallback(
    (order) => {
      const id = order?.store;
      if (!id) return "";
      const match = stores.find(
        (s) => s?._id === id || String(s?.admin) === String(id)
      );
      return match?.name || "";
    },
    [stores]
  );

  // Handle close modal
  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setOrderToDelete(null);
    setIsProcessingReject(false);
  };

  // Toggle fullscreen mode
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  }, []);

  // Handle fullscreen change event
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Audio setup - notifications always enabled
  const playNotification = useCallback(() => {
    if (!audioRef.current) return;

    try {
      // Create a new audio element each time to ensure it plays even if previous one is still playing
      const audio = new Audio("/notification.mp3");
      audio.volume = 0.3;

      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.error("Error playing notification sound:", err);
        });
      }
    } catch (error) {
      console.error("Error with audio playback:", error);
    }
  }, []);

  // Fetch printer configs
  const fetchPrinterConfigs = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/printer-settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrinterConfigs(response.data.data || []);
    } catch (err) {
      console.error("Error fetching printer configs:", err);
      setPrinterConfigs([]);
    }
  };

  const fetchPlatforms = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/platforms", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.success && response.data.data) {
        setRawPlatforms(response.data.data.platforms || []);
      }
    } catch (error) {
      console.error("Error fetching platforms:", error);
    }
  };

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Ensure response has the expected structure
      if (!response.data || !Array.isArray(response.data.stores)) {
        throw new Error("Invalid stores data in response");
      }

      console.log("Stores loaded:", response.data.stores);

      const allStores = response.data.stores;

      if (allStores.length > 0) {
        // Set the first store as default
        setStoreId(allStores[0]._id);

        // Process branches from all stores
        const allBranches = allStores.flatMap((store) => {
          const storeBranches = store.branches || [];
          const storeName = store.name || "Unknown Store";
          return storeBranches.map((branch) => {
            const localizedName = isArabic
              ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
              : branch?.name?.en || branch?.name;
            return {
              ...branch,
              storeId: store._id,
              storeName: storeName,
              displayName: `${localizedName} - ${storeName}`,
            };
          });
        });

        console.log("All branches loaded from stores:", allBranches);
        setBranches(allBranches);
        // Set selected branch - only use saved branch if it exists in current branches
        if (selectedBranch) {
          // Check if saved branch still exists in current branches
          const branchToSelect = allBranches.find(b => b._id === selectedBranch._id);
          if (branchToSelect) {
            setSelectedBranch(branchToSelect);
            saveBranchToStorage(branchToSelect);
          }
        }
      }

      setStores(allStores);
      return allStores;
    } catch (error) {
      console.error(
        "Error fetching stores:",
        error.response?.data || error.message
      );
      setBranches([]);
      return [];
    }
  };

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      fetchPlatforms();
      const stores = await fetchStores(); // Wait for stores to load
      fetchPrinterConfigs();

      // Now get the first branch after stores are loaded
      if (stores && stores.length > 0) {
        const allBranches = stores.flatMap((store) => {
          const storeBranches = store.branches || [];
          return storeBranches.map((branch) => {
            const localizedName = isArabic
              ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
              : branch?.name?.en || branch?.name;
            const storeDisplay = store.name || "Unknown Store";
            return {
              ...branch,
              storeId: store._id,
              storeName: storeDisplay,
              displayName: `${localizedName} - ${storeDisplay}`,
            };
          });
        });

        // Fetch orders - only use branch ID if selectedBranch has a valid _id
        const shouldUseBranch = selectedBranch && selectedBranch._id && selectedBranch._id.trim() !== "";
        const branchIdToUse = shouldUseBranch 
          ? (allBranches.find(b => b._id === selectedBranch._id)?._id || null)
          : null;
        await fetchOrders(1, "all", true, branchIdToUse);
        initialFetchDoneRef.current = true;
      } else {
        // No branches available, fetch all orders
        await fetchOrders(1, "all", true, null);
        initialFetchDoneRef.current = true;
      }
    };

    loadInitialData();
  }, []);

  // Main fetch function
  const fetchOrders = useCallback(
    async (
      pageNum,
      filter = "all",
      reset = false,
      branchId = null,
      isFilterChange = false,
      dateRangeOverride = undefined
    ) => {
      console.log("Fetching orders....", branchId);

      // For "ready" filter, only allow first page and disable pagination
      if (filter === "ready" && pageNum > 1) {
        return;
      }

      if (fetchingRef.current && !reset) return;
      fetchingRef.current = true;

      // Use different loading state for filter changes
      if (isFilterChange) {
        setFilterLoading(true);
      } else {
        setLoading(true);
      }

      try {
        const token = localStorage.getItem("token");
        // For "ready" filter, limit to 10 items
        const limit = filter === "ready" ? 10 : 20;
        let url = `/ods-orders?page=${pageNum}&limit=${limit}`;

        // Add branch filter if a branch is selected
        if (branchId) {
          url += `&branch=${branchId}`;
        }

        const platformParam = (selectedPlatform || "");
        if (platformParam && platformParam !== "All") {
          url += `&delivery_service=${encodeURIComponent(platformParam)}`;
        }

        let loopsStatuses = [];

        // Apply status filter logic
        switch (filter) {
          case "in_progress":
            loopsStatuses = ["ACCEPTED"];
            break;
          case "pending":
            loopsStatuses = ["PENDING"];
            break;
          case "ready":
            // Backend now exposes READY state as COMPLETED in loops_status
            loopsStatuses = ["COMPLETED"];
            break;
          case "delivered":
            loopsStatuses = ["DELIVERED"];
            break;
          case "new": {
            const thirtyMinutesAgo = new Date();
            thirtyMinutesAgo.setMinutes(thirtyMinutesAgo.getMinutes() - 30);
            url += `&fromDate=${thirtyMinutesAgo.toISOString()}`;
            break;
          }
          case "all":
            loopsStatuses = ["PENDING", "ACCEPTED", "COMPLETED"];
            break;
          default:
            break;
        }

        // If we have loops_statuses, append them
        if (loopsStatuses.length > 0) {
          url += `&loops_statuses=${loopsStatuses.join(",")}`;
        }

        // Apply date preset range as YYYY-MM-DD without time
        const formatDateOnly = (d) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          return `${y}-${m}-${dd}`;
        };

        const computeDateOnlyRange = () => {
          if (dateRangeOverride) {
            return {
              from: dateRangeOverride.from || null,
              to: dateRangeOverride.to || null,
            };
          }
          const now = new Date();
          const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
          const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
          if (datePreset === "today") {
            return { from: formatDateOnly(startOfDay(now)), to: formatDateOnly(endOfDay(now)) };
          }
          if (datePreset === "yesterday") {
            const y = new Date(now);
            y.setDate(y.getDate() - 1);
            return { from: formatDateOnly(startOfDay(y)), to: formatDateOnly(endOfDay(y)) };
          }
          if (datePreset === "last2") {
            const s = new Date(now);
            s.setDate(s.getDate() - 1);
            return { from: formatDateOnly(startOfDay(s)), to: formatDateOnly(endOfDay(now)) };
          }
          if (datePreset === "last7") {
            const s = new Date(now);
            s.setDate(s.getDate() - 6);
            return { from: formatDateOnly(startOfDay(s)), to: formatDateOnly(endOfDay(now)) };
          }
          if (datePreset === "last30") {
            const s = new Date(now);
            s.setDate(s.getDate() - 29);
            return { from: formatDateOnly(startOfDay(s)), to: formatDateOnly(endOfDay(now)) };
          }
          if (datePreset === "customRange") {
            if (dateFrom && dateTo) {
              return { from: dateFrom, to: dateTo };
            }
            if (dateFrom && !dateTo) {
              return { from: dateFrom, to: null };
            }
            if (!dateFrom && dateTo) {
              return { from: null, to: dateTo };
            }
          }
          return null;
        };

        const dateOnlyRange = computeDateOnlyRange();
        if (dateOnlyRange && filter !== "new") {
          if (dateOnlyRange.from) {
            url += `&start_date=${dateOnlyRange.from}`;
          }
          if (dateOnlyRange.to) {
            url += `&end_date=${dateOnlyRange.to}`;
          }
        }

        console.log("Fetching orders with URL:", url);

        const response = await api.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const {
          orders: fetchedOrders = [],
          totalOrders,
          totalPages,
          statusCounts,
        } = response.data;

        if (reset) {
          setOrders(fetchedOrders);
          setOrderStats(statusCounts || {});
        } else {
          setOrders((prev) => [...prev, ...fetchedOrders]);
        }

        // For "ready" filter, disable pagination (no more pages)
        if (filter === "ready") {
          setHasMore(false);
        } else {
          // Check if there are more pages
          setHasMore(pageNum < totalPages && fetchedOrders.length === limit);
        }
      } catch (err) {
        console.error("Failed to fetch orders", err);
        if (reset) {
          setOrders([]);
          setOrderStats({});
        }
        setHasMore(false);
      } finally {
        setLoading(false);
        setInitialLoading(false);
        setFilterLoading(false);
        fetchingRef.current = false;
      }
    }, [selectedPlatform, datePreset, dateFrom, dateTo]
  );

  const reloadDisplayOrdersRef = useRef(() => {});
  reloadDisplayOrdersRef.current = () => {
    const branchId = selectedBranch?._id === "all" ? null : selectedBranch?._id;
    fetchOrders(1, activeFilter, true, branchId, false);
  };
  useOrdersPageSession(useCallback(() => reloadDisplayOrdersRef.current(), []));

  // Update order stats after status change
  const updateOrderStats = useCallback((oldStatus, newStatus) => {
    setOrderStats((prev) => {
      const updated = { ...prev };

      // Normalize frontend labels to backend loops_status keys
      const normalizedOld = oldStatus === "READY FOR DELIVERY" ? "COMPLETED" : oldStatus;
      const normalizedNew = newStatus === "READY FOR DELIVERY" ? "COMPLETED" : newStatus;

      // Decrease count for old status
      if (updated[normalizedOld] && updated[normalizedOld] > 0) {
        updated[normalizedOld]--;
      }

      // Increase count for new status only if it's not REJECTED/CANCELLED
      if (normalizedNew !== "REJECTED" && normalizedNew !== "CANCELLED") {
        if (updated[normalizedNew]) {
          updated[normalizedNew]++;
        } else {
          updated[normalizedNew] = 1;
        }
      }

      return updated;
    });
  }, []);

  // Handle branch selection
  const handleBranchSelect = useCallback(
    (branch) => {
      setSelectedBranch(branch);
      saveBranchToStorage(branch);

      // Reset pagination and fetch orders for the selected branch
      setPage(1);
      setOrders([]);
      setHasMore(true);

      // Pass the branch ID to fetchOrders to filter orders by branch
      const branchId = branch?._id === "all" ? null : branch?._id;
      console.log("🏢 [ODS] Fetching orders for branch ID:", branchId);
      fetchOrders(1, activeFilter, true, branchId, true);
    },
    [activeFilter, fetchOrders]
  );

  // Handle filter change
  const handleFilterChange = useCallback(
    (filter) => {
      setActiveFilter(filter);
      currentFilterRef.current = filter;
      setPage(1);
      setHasMore(true);
      setOrders([]); // Clear orders immediately to show skeleton
      fetchingRef.current = false;

      // Get the current branch ID (or null if 'All Branches' is selected)
      const branchId =
        selectedBranch?._id === "all" ? null : selectedBranch?._id;

      // Fetch orders with the current branch filter
      console.log("Filter changed, fetching with branch ID:", branchId);
      fetchOrders(1, filter, true, branchId, true);
    },
    [fetchOrders, selectedBranch]
  );

  // Apply date preset changes
  const applyDatePreset = useCallback((dateRange) => {
    setPage(1);
    setHasMore(true);
    setOrders([]);
    fetchingRef.current = false;
    const branchId = selectedBranch?._id === "all" ? null : selectedBranch?._id;
    fetchOrders(1, activeFilter, true, branchId, true, dateRange);
  }, [activeFilter, fetchOrders, selectedBranch]);

  useEffect(() => {
    if (!initialFetchDoneRef.current) return;
    if (datePreset !== "customRange") {
      applyDatePreset();
    }
  }, [datePreset]);

  useEffect(() => {
    if (!initialFetchDoneRef.current) return;
    applyDatePreset();
  }, [selectedPlatform]);

  // Auto-apply when either date is present; skip when both cleared
  useEffect(() => {
    if (!initialFetchDoneRef.current) return;
    if (!dateFrom && !dateTo) return;
    setDatePreset("customRange");
    applyDatePreset({ from: dateFrom || null, to: dateTo || null });
  }, [dateFrom, dateTo]);

  // Expose filter data to TopNavbar via window
  useEffect(() => {
    window.ordersDisplayFilters = {
      activeFilter,
      orderStats,
      handleFilterChange,
      isFullscreen,
    };

    // Dispatch event to notify TopNavbar
    window.dispatchEvent(new CustomEvent("ordersDisplayFiltersUpdated"));

    return () => {
      delete window.ordersDisplayFilters;
    };
  }, [activeFilter, orderStats, handleFilterChange, isFullscreen]);

  // Load more when page changes (except for initial load)
  useEffect(() => {
    if (page > 1) {
      const branchId =
        selectedBranch?._id === "all" ? null : selectedBranch?._id;
      fetchOrders(page, currentFilterRef.current, false, branchId, false);
    }
  }, [page, selectedBranch]);

  // Intersection observer for infinite scroll
  const lastOrderRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !fetchingRef.current) {
            setPage((prev) => prev + 1);
          }
        },
        {
          root: null,
          rootMargin: "500px", // Load 500px before reaching the element
          threshold: 0,
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore]
  );

  // Listen to global order refresh trigger from OrderNotificationContext
  useEffect(() => {
    if (orderRefreshTrigger > 0) {
      console.log(
        "📨 [OrdersDisplay] New order detected from global context, refreshing orders..."
      );
      const branchId =
        selectedBranch?._id === "all" ? null : selectedBranch?._id;
      fetchOrders(1, activeFilter, true, branchId, false);
    }
  }, [
    orderRefreshTrigger,
    playNotification,
    activeFilter,
    selectedBranch,
    fetchOrders,
  ]);

  // Audio setup - simplified since we create new Audio instances in playNotification
  useEffect(() => {
    return () => {
      // Cleanup any existing audio elements
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const getStatusColor = (status) => {
    if (status === "CANCELLED" || status === "REJECTED")
      return "status-rejected";
    if (status === "DELIVERED") return "status-delivered";
    if (status === "COMPLETED") return "status-ready-blue";
    if (status === "ACCEPTED") return "status-in-progress-orange";
    if (status === "PENDING") return "status-pending-gray";
    return "status-default";
  };

  // Handle expired order actions - separate function for clarity

  const handleAction = async (appId, orderId, action) => {
    // Find the current order (declared outside try-catch for access in both blocks)
    const currentOrder = orders.find((o) => o._id === orderId);

    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      // Add order to updating set to show loading state
      setUpdatingOrders((prev) => new Set(prev).add(orderId));

      const oldStatus = currentOrder?.loops_status;

      // For REJECTED status, immediately remove from view with animation
      if (action === "REJECTED") {
        // Add fade-out animation
        const orderElement = document.querySelector(
          `[data-order-id="${orderId}"]`
        );
        if (orderElement) {
          orderElement.style.transition =
            "opacity 0.3s ease, transform 0.3s ease";
          orderElement.style.opacity = "0";
          orderElement.style.transform = "scale(0.95)";
        }

        // Remove after animation completes
        setTimeout(() => {
          setOrders((prevOrders) =>
            prevOrders.filter((order) => order._id !== orderId)
          );
        }, 300);

        // Update stats
        if (oldStatus) {
          updateOrderStats(oldStatus, action);
        }
      } else {
        // For other statuses, optimistically update
        setOrders((prevOrders) =>
          prevOrders.map((order) => {
            if (order._id === orderId) {
              // When marking Ready, UI renders off loops_status. Reflect immediately.
              if (action === "READY FOR DELIVERY") {
                return {
                  ...order,
                  status: "READY FOR DELIVERY",
                  loops_status: "COMPLETED",
                };
              }
              return { ...order, status: action };
            }
            return order;
          })
        );

        // Update stats optimistically
        if (oldStatus) {
          updateOrderStats(oldStatus, action);
        }

        // Check if should remove from current filter view
        const shouldRemoveFromView = () => {
          if (activeFilter === "all") return false;
          if (activeFilter === "pending" && action !== "PENDING") return true;
          if (activeFilter === "in_progress" && action !== "ACCEPTED")
            return true;
          if (activeFilter === "ready" && action !== "READY FOR DELIVERY")
            return true;
          if (activeFilter === "delivered" && action !== "DELIVERED")
            return true;
          return false;
        };

        if (shouldRemoveFromView()) {
          // Add fade-out animation
          const orderElement = document.querySelector(
            `[data-order-id="${orderId}"]`
          );
          if (orderElement) {
            orderElement.style.transition =
              "opacity 0.3s ease, transform 0.3s ease";
            orderElement.style.opacity = "0";
            orderElement.style.transform = "scale(0.95)";
          }

          // Remove after animation
          setTimeout(() => {
            setOrders((prevOrders) =>
              prevOrders.filter((order) => order._id !== orderId)
            );
          }, 300);
        }
      }

      // Make API call
      const token = localStorage.getItem("token");
      // Map frontend action to backend loops_status when needed
      const mappedLoopsStatus =
        action === "READY FOR DELIVERY" ? "COMPLETED" : action;
      const response = await api.patch(
        `/orders/${orderId}/status`,
        {
          status: action,
          loops_status: mappedLoopsStatus,
          appId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // If the API call fails, we should revert the optimistic update
      if (!response.data || response.data.error) {
        throw new Error("Failed to update order status");
      }

      // Show success toast and reflect backend response for Accept/Ready/Reject
      if (action === "ACCEPTED") {
        toast.success(
          `${t("Order accepted successfully")} - Order #${getExternalOrderId(
            currentOrder
          )}`,
          { autoClose: 2000 }
        );

        // Reflect backend status change immediately in UI
        const updatedOrder = response?.data?.order;
        if (updatedOrder) {
          setOrders((prevOrders) =>
            prevOrders.map((order) =>
              order._id === orderId
                ? {
                    ...order,
                    status: updatedOrder.status || action,
                    loops_status: updatedOrder.loops_status || "ACCEPTED",
                  }
                : order
            )
          );
        }
      } else if (action === "READY FOR DELIVERY") {
        toast.success(
          `${t("Order marked ready successfully")} - Order #${getExternalOrderId(
            currentOrder
          )}`,
          { autoClose: 2000 }
        );

        const updatedOrder = response?.data?.order;
        if (updatedOrder) {
          setOrders((prevOrders) =>
            prevOrders.map((order) =>
              order._id === orderId
                ? {
                    ...order,
                    status: updatedOrder.status || "READY FOR DELIVERY",
                    loops_status: updatedOrder.loops_status || "COMPLETED",
                  }
                : order
            )
          );
        }
      } else if (action === "REJECTED") {
        toast.success(
          `${t("Order rejected successfully")} - Order #${getExternalOrderId(
            currentOrder
          )}`,
          { autoClose: 2000 }
        );
      }
    } catch (error) {
      console.error(
        "Error updating order status:",
        error.response?.data || error.message
      );

      // Show error toast
      if (action === "ACCEPTED") {
        const apiData = error.response?.data || {};
        const detail =
          (i18n?.language || "").startsWith("ar") && apiData.message_ar
            ? apiData.message_ar
            : apiData.message || apiData.error_code || null;
        toast.error(
          detail
            ? `${detail} - Order #${getExternalOrderId(currentOrder)}`
            : `${t("Failed to accept order")} - Order #${getExternalOrderId(
                currentOrder
              )}`,
          { autoClose: 5000 }
        );
      } else if (action === "READY FOR DELIVERY") {
        toast.error(
          `${t("Failed to mark order ready")} - Order #${getExternalOrderId(
            currentOrder
          )}`,
          { autoClose: 3000 }
        );
      } else if (action === "REJECTED") {
        toast.error(
          `${t("Failed to reject order")} - Order #${getExternalOrderId(
            currentOrder
          )}`,
          { autoClose: 3000 }
        );
      } else {
        toast.error(
          `${t("Failed to update order")} - Order #${getExternalOrderId(
            currentOrder
          )}`,
          { autoClose: 3000 }
        );
      }

      // Revert the optimistic update on error
      const branchId =
        selectedBranch?._id === "all" ? null : selectedBranch?._id;
      fetchOrders(1, activeFilter, true, branchId, false);
    } finally {
      // Remove order from updating set
      setUpdatingOrders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handlePrint = (order) => {
    if (printerConfigs.length === 0) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(
        `<h2>No printer configurations available</h2>
         <p>Please configure printer settings in the admin panel first.</p>`
      );
      printWindow.document.close();
      return;
    }

    const branchId = order.branch || order.branchId;
    let config = null;

    if (branchId) {
      config = printerConfigs.find(
        (cfg) =>
          cfg.branchId === branchId || cfg.branchId === branchId.toString()
      );

      if (!config) {
        config = printerConfigs[0];
      }
    } else {
      config = printerConfigs[0];
    }

    if (!config) {
      console.error("No printer configurations available");
      return;
    }

    const numberOfCopies = config.numberOfCopies || 1;
    const paperStyle =
      config.paperSize === "A4"
        ? "width: 210mm;"
        : config.paperSize === "A5"
        ? "width: 148mm;"
        : "width: 105mm;";
    const logoHtml = config.logo
      ? `<img src="${config.logo}" alt="Logo" style="max-width:80px;max-height:80px;display:block;margin:0 auto 8px auto;" />`
      : "";
    const headerHtml = config.receipt_header
      ? `<div style="text-align:center;font-weight:bold;margin-bottom:8px;">${formatPlatformName(order.platform, t)}</div>`
      : "";
    const footerHtml = config.receipt_footer
      ? `<div style="text-align:center;font-size:12px;margin-top:12px;">${config.receipt_footer}</div>`
      : "";
    const orderRows = order.products
      .map(
        (product) => `
            <tr>
                <td>${product.quantity} x ${
          product.loops_product_id?.name?.en ||
          product.loops_product_id?.name?.ar ||
          product.product_details?.name?.en ||
          product.product_details?.name?.ar ||
          product.name ||
          product.product_id
        }</td>
                <td style="text-align:right;">${product.final_price.toFixed(
                  2
                )} ${config.currency || "SAR"}</td>
            </tr>
            ${
              product.modifiers && product.modifiers.length > 0
                ? product.modifiers
                    .map((mod) =>
                      mod.options
                        .map(
                          (opt) =>
                            `<tr><td style='padding-left:16px;'>+ ${
                              opt.quantity
                            } x ${
                              opt.nameEn || opt.nameAr || opt.name
                            }</td><td style='text-align:right;'>${opt.final_price.toFixed(
                              2
                            )} ${config.currency || "SAR"}</td></tr>`
                        )
                        .join("")
                    )
                    .join("")
                : ""
            }
        `
      )
      .join("");
    const discountHtml = order.offer
      ? `<tr><td>Discount</td><td style='text-align:right;'>- ${order.offer.amount.toFixed(
          2
        )} ${config.currency || "SAR"}</td></tr>`
      : "";
    const subtotalHtml = config.receipt_show_subtotal
      ? `<tr><td>Subtotal</td><td style='text-align:right;'>${order.price.toFixed(
          2
        )} ${config.currency || "SAR"}</td></tr>`
      : "";
    const totalHtml = `<tr style='font-weight:bold;'><td>Total</td><td style='text-align:right;'>${order.final_price.toFixed(
      2
    )} ${config.currency || "SAR"}</td></tr>`;

    const branchName = order.branchName || config.branchName || "";
    const storeName = getStoreNameFromOrder(order) || "";
    const branchDisplayNameFromOrder =
      branchName && storeName
        ? `${branchName} - ${storeName}`
        : branchName || storeName || "";
    const selectedBranchDisplayName =
      selectedBranch && selectedBranch._id !== "all"
        ? selectedBranch.displayName || (() => {
            const localizedSelectedName = isArabic
              ? selectedBranch?.name?.ar || selectedBranch?.name_ar || selectedBranch?.name?.en || selectedBranch?.name
              : selectedBranch?.name?.en || selectedBranch?.name;
            const storePart = selectedBranch.storeName ? ` - ${selectedBranch.storeName}` : "";
            return localizedSelectedName ? `${localizedSelectedName}${storePart}` : "";
          })()
        : "";
    const branchDisplayName =
      selectedBranchDisplayName || branchDisplayNameFromOrder;
    const html = `
            <html><head><title>Print Order</title>
            <style>
                body { font-family: Arial, sans-serif; background: #fff; }
                .receipt { ${paperStyle} margin: 0 auto; padding: 16px; border: 1px solid #eee; }
                .receipt h2 { text-align: center; margin: 0 0 8px 0; }
                .receipt table { width: 100%; border-collapse: collapse; margin-top: 8px; }
                .receipt td { padding: 4px 0; font-size: 14px; }
                .receipt .meta { font-size: 13px; margin-bottom: 8px; }
                .receipt .footer { margin-top: 16px; font-size: 12px; text-align: center; color: #888; }
            </style>
            </head><body onload="window.print()">
            <div class="receipt">
                ${logoHtml}
                <h2>${storeName}</h2>
                <div class="meta">Branch: ${branchDisplayName}<br>Order #: ${getExternalOrderId(
      order
    )}<br>Date: ${new Date(order.createdAt).toLocaleString()}</div>
                ${headerHtml}
                <table>
                    ${orderRows}
                    ${discountHtml}
                    ${subtotalHtml}
                    ${totalHtml}
                </table>
                ${footerHtml}
                <div class="footer">Printer: ${config.printerType}, Paper: ${
      config.paperSize
    }</div>
            </div>
            </body></html>
        `;
    for (let i = 0; i < numberOfCopies; i++) {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        continue;
      }
      printWindow.document.write(html);
      printWindow.document.close();
    }
  };

  // Skeleton loading component
  const renderSkeletonLoader = () => (
    <div className="orders-grid">
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-sm p-4 mb-4"
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-2">
              <Skeleton width={60} height={28} />
              <div>
                <Skeleton width={100} height={16} className="mb-1" />
                <Skeleton width={80} height={14} />
              </div>
            </div>
            <Skeleton circle width={24} height={24} />
          </div>

          <div className="d-flex justify-content-between mb-3">
            <Skeleton width={80} height={20} />
            <Skeleton width={40} height={20} />
          </div>

          <hr className="my-2" />

          <div className="mb-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="mb-2">
                <Skeleton width={`${80 + Math.random() * 20}%`} height={16} />
                <Skeleton width="60%" height={14} className="mt-1" />
              </div>
            ))}
          </div>

          <div className="d-flex gap-2">
            <Skeleton width="100%" height={36} borderRadius={5} />
            <Skeleton width="100%" height={36} borderRadius={5} />
          </div>
        </div>
      ))}
    </div>
  );

  // Skeleton loader for stats
  const renderStatsSkeleton = () => (
    <div className="d-flex gap-4 justify-content-center mb-4">
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} width={120} height={36} borderRadius={10} />
      ))}
    </div>
  );

  if (initialLoading) {
    return (
      <section className="orders-display-page">
        {renderStatsSkeleton()}
        {renderSkeletonLoader()}
      </section>
    );
  }

  return (
    <section
      ref={containerRef}
      className={`orders-display-page ${isFullscreen ? "fullscreen-mode" : ""}`}
      style={{
        minHeight: "100vh",
        backgroundColor: "#F7F7F7",
        ...(isFullscreen
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#F7F7F7",
              zIndex: 9999,
              overflowY: "auto",
            }
          : undefined),
      }}
    >
      <style jsx global>{`
        .status-rejected {
          background-color: #E21D1D !important;
          border-color: #E21D1D !important;
        }
        .status-delivered {
          background-color: #00D69F !important;
          border-color: #00D69F !important;
        }
        .status-ready-blue {
          background-color: #118AB2 !important;
          border-color: #118AB2 !important;
        }
        .status-in-progress-orange {
          background-color: #00D69F !important;
          border-color: #00A379 !important;
        }
        .status-pending-gray {
          background-color: #5E5E5E !important;
          border-color: #5E5E5E !important;
        }
        .status-default {
          background-color: #083B4C !important;
          border-color: #083B4C !important;
        }

        .fullscreen-header {
          display: flex;
          flex-wrap: wrap;
        }

        .filter-label { height: 36px; display: inline-flex; align-items: center; }
        .filters-row .custom-filter-button,
        .buttons-row .custom-filter-button { height: 36px !important; }

        .order-updating {
          opacity: 0.6;
          pointer-events: none;
          transition: opacity 0.3s ease;
        }

        .order-card {
          transition: all 0.3s ease;
        }

        @media (max-width: 1200px) {
          .fullscreen-header > div:nth-child(2) {
            flex: 1 1 100% !important;
            justify-content: center !important;
            margin-top: 8px;
          }
        }

        @media (max-width: 768px) {
          .fullscreen-header > div:nth-child(2) {
            flex-wrap: wrap !important;
          }
          .buttons-wrapper {
            flex-wrap: wrap;
            gap: 5px;
          }
          .filter-btn {
            padding: 5px 10px !important;
            font-size: 0.8rem !important;
          }
          .controls-row {
            flex-direction: column;
            gap: 10px;
          }
          .dropdown-container {
            width: 100%;
          }
        }

        @media (max-width: 810px) {
          .filters-row,
          .buttons-row { flex-wrap: wrap !important; }
          .filters-row .filter-group,
          .buttons-row .filter-group { flex: 1 1 100% !important; width: 100% !important; }
        }

        @media (max-width: 555px) {
          .filters-row .filter-group,
          .buttons-row .filter-group { flex-wrap: wrap !important; flex-direction: column !important; align-items: flex-start !important; }
          .filter-group .platform-filter-block,
          .filter-group .date-filter-block { flex: 1 1 100% !important; min-width: 100% !important; }
          .filters-row .filter-group,
          .buttons-row .filter-group { gap: 12px !important; }
          .filter-label { width: 100px !important; }
        }
      `}</style>

      {/* Render notification inside fullscreen container when in fullscreen mode */}
      {isFullscreen && showNotification && newOrder && (
        <GlobalOrderNotification
          order={newOrder}
          onView={handleViewOrder}
          onDismiss={handleDismiss}
          usePortal={false}
          hideViewButton={true}
        />
      )}

      {/* Fullscreen Header - Logo, Filters */}
      {isFullscreen && (
        <div
          className="fullscreen-header"
          style={{
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: "white",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            zIndex: 10001,
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            minHeight: "56px",
          }}
        >
          {/* Left side - Menu + Logo */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              zIndex: 1,
            }}
          >
            <span
              style={{
                cursor: "pointer",
                background: "transparent",
                borderRadius: "20%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                color: "#000",
              }}
              onClick={() => navigate("/dashboard")}
            >
              <Menu size={20} />
            </span>

            <img
              src={logo}
              alt="Company Logo"
              style={{ height: "32px", width: "auto", cursor: "pointer" }}
              onClick={() => navigate("/dashboard")}
            />
          </div>

          {/* Center - Filter Buttons (absolutely centered to full width) */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: "12px",
              flexWrap: "nowrap",
              alignItems: "center",
            }}
          >
            <button
              onClick={() => handleFilterChange("all")}
              className={`filter-btn ${
                activeFilter === "all" ? "active orange" : "orange"
              }`}
            >
              {t("all")}
            </button>

            <button
              onClick={() => handleFilterChange("pending")}
              className={`filter-btn ${
                activeFilter === "pending" ? "active" : ""
              }`}
            >
              {t("New")} <span>{orderStats?.PENDING || 0}</span>
            </button>

            <button
              onClick={() => handleFilterChange("in_progress")}
              className={`filter-btn ${
                activeFilter === "in_progress" ? "active orange" : "orange"
              }`}
            >
              {t("inProgress")} <span>{orderStats?.ACCEPTED || 0}</span>
            </button>

            <button
              onClick={() => handleFilterChange("ready")}
              className={`filter-btn ${
                activeFilter === "ready" ? "active blue" : "blue"
              }`}
            >
              {t("completed")}
            </button>
          </div>
        </div>
      )}

      {/* Controls Row - Show when in fullscreen mode */}
      {isFullscreen && (
        <div className="mb-3">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            {/* Branch Dropdown - Full width on mobile, auto on larger screens */}
            <div style={{ minWidth: "310px", flex: "0 1 auto" }}>
              <CustomDropdown
                options={[
                  { _id: "all", name: t("allBranches", "All Branches") },
                  ...branches.map((branch) => ({
                    ...branch,
                    name:
                      branch.displayName ||
                      (isArabic
                        ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
                        : branch?.name?.en || branch?.name),
                  })),
                ]}
                selectedOption={
                  selectedBranch || { _id: "all", name: t("allBranches", "All Branches") }
                }
                handleSelect={(option) => {
                  if (option._id === "all") {
                    handleBranchSelect(null);
                  } else {
                    handleBranchSelect(option);
                  }
                }}
                placeholder={t("Select Branch")}
                label={t("branches")}
                showClear={false}
              />
            </div>

            {/* Buttons Row */}
            <div
              className="d-flex justify-content-end filters-row"
              style={{ flex: "0 0 auto", gap: "6px", alignItems: "center" }}
            >
              <div className="filter-group d-flex align-items-center gap-2" style={{ flex: "1 1 auto" }}>
                <div style={{minWidth: "220px" }} className="platform-filter-block d-flex align-items-center gap-2">
                  <span className="filter-label" style={{ color: "#0d2d3c", fontWeight: 500, fontSize: 14 }}>{t("toolBar.platform")}</span>
                  <FilterDropdown
                    orderStatuses={platformOptions}
                    activeTab={selectedPlatform}
                    className="flex-item"
                    showLabel={false}
                    onSelectItem={(id) => {
                      setSelectedPlatform(id);
                    }}
                  />
                </div>
                <div style={{ minWidth: "200px" }} className="date-filter-block d-flex align-items-center gap-2">
                  <span className="filter-label" style={{ color: "#0d2d3c", fontWeight: 500, fontSize: 14 }}>{t("toolBar.date")}</span>
                  <FilterDropdown
                    orderStatuses={datePresetOptions}
                    activeTab={datePreset}
                    setActiveTab={(id) => setDatePreset(id)}
                    className="flex-item"
                    showLabel={false}
                    onSelectItem={(id) => {
                      setDatePreset(id);
                      if (id !== "customRange") {
                        setDateFrom("");
                        setDateTo("");
                      }
                    }}
                    shouldCloseOnSelect={(id) => id !== "customRange"}
                    renderExtraMenu={(close) => (
                      <div className="p-2">
                          <div className="mb-2">
                            <small className="text-muted">{t("toolBar.dateFrom")}</small>
                            <input
                              type="date"
                              value={dateFrom}
                              onChange={(e) => {
                                const val = e.target.value;
                                setDateFrom(val);
                                if (val && dateTo) {
                                  setDatePreset("customRange");
                                  applyDatePreset({ from: val || null, to: dateTo || null });
                                  close();
                                }
                              }}
                              onClick={(e) => {
                                if (e.currentTarget.showPicker) {
                                  e.currentTarget.showPicker();
                                }
                              }}
                              className="form-control form-control-sm"
                            />
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">{t("toolBar.dateTo")}</small>
                            <input
                              type="date"
                              value={dateTo}
                              onChange={(e) => {
                                const val = e.target.value;
                                setDateTo(val);
                                if (dateFrom && val) {
                                  setDatePreset("customRange");
                                  applyDatePreset({ from: dateFrom || null, to: val || null });
                                  close();
                                }
                              }}
                              onClick={(e) => {
                                if (e.currentTarget.showPicker) {
                                  e.currentTarget.showPicker();
                                }
                              }}
                              className="form-control form-control-sm"
                            />
                          </div>
                          <div>
                            <Button
                              color="secondary"
                              size="sm"
                              className="w-100"
                              onClick={() => {
                                setDateFrom("");
                                setDateTo("");
                                setDatePreset("today");
                                close();
                              }}
                            >
                              {t("cancel")}
                            </Button>
                          </div>
                        </div>
                    )}
                  />
                </div>
              </div>
              {/* Sound toggle - styled like Orders page */}
              <button
                className={`actionbar-btn ${
                  soundEnabled ? "sound-on" : "sound-off"
                }`}
                onClick={toggleSound}
                style={{ minHeight: "36px", padding: "8px 12px" }}
                aria-label={soundEnabled ? t("disableSound") : t("enableSound")}
                title={soundEnabled ? t("disableSound") : t("enableSound")}
              >
                <span className="btn-icon">
                  {soundEnabled ? (
                    <svg
                      className="oh-icon-directional"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path
                        d="M15 6.3706C15 4.65827 12.9884 3.73774 11.6926 4.85712L8.36317 7.73321C7.99988 8.04704 7.53583 8.21972 7.05576 8.21973L5.49998 8.21974C4.11928 8.21975 3 9.33903 3 10.7197V14.0127C3 15.3934 4.11929 16.5127 5.5 16.5127H7.0558C7.53587 16.5127 7.99993 16.6854 8.36322 16.9992L11.6926 19.8753C12.9884 20.9947 15 20.0741 15 18.3618V12.3662V6.3706Z"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      />
                      <path
                        d="M18 15.3672C18.6279 14.5315 19 13.4928 19 12.3672C19 11.2416 18.6279 10.2028 18 9.36719"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M20 18.3672C21.2558 16.6959 22 14.6184 22 12.3672C22 10.116 21.2558 8.03851 20 6.36719"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="oh-icon-directional"
                      width="16"
                      height="16"
                      viewBox="0 0 18 21"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path
                        d="M13.75 11C13.75 10.5858 13.4142 10.25 13 10.25C12.5858 10.25 12.25 10.5858 12.25 11H13H13.75ZM9.40001 17.6226L9.89029 17.055L9.8799 17.046L9.86918 17.0374L9.40001 17.6226ZM8.46917 15.9149C8.14601 15.6558 7.67399 15.7077 7.41487 16.0308C7.15575 16.354 7.20767 16.826 7.53083 17.0851L8 16.5L8.46917 15.9149ZM3.49998 6.21974L3.49998 5.46974L3.49998 6.21974ZM9.69256 17.8753L10.1828 17.3077L9.69256 17.8753ZM5.05576 6.21973L5.05576 5.46973L5.05576 6.21973ZM6.36317 5.73321L6.85346 6.30077L10.1828 3.42467L9.69256 2.85712L9.20228 2.28956L5.87289 5.16565L6.36317 5.73321ZM3.49998 6.21974L3.49999 6.96974L5.05577 6.96973L5.05576 6.21973L5.05576 5.46973L3.49998 5.46974L3.49998 6.21974ZM1 12.0127H1.75V8.71974H1H0.25V12.0127H1ZM5.80003 14.5127V13.7627H3.5V14.5127V15.2627H5.80003V14.5127ZM13 12.5H12.25V16.3618H13H13.75V12.5H13ZM13 4.3706H12.25V7H13H13.75V4.3706H13ZM13 11H12.25V12.5H13H13.75V11H13ZM9.69256 17.8753L10.1828 17.3077L9.89029 17.055L9.40001 17.6226L8.90972 18.1901L9.20228 18.4428L9.69256 17.8753ZM9.40001 17.6226L9.86918 17.0374L8.46917 15.9149L8 16.5L7.53083 17.0851L8.93084 18.2077L9.40001 17.6226ZM1 12.0127H0.25C0.25 13.8076 1.70507 15.2627 3.5 15.2627V14.5127V13.7627C2.5335 13.7627 1.75 12.9792 1.75 12.0127H1ZM3.49998 6.21974L3.49998 5.46974C1.70506 5.46975 0.25 6.92482 0.25 8.71974H1H1.75C1.75 7.75324 2.53349 6.96974 3.49999 6.96974L3.49998 6.21974ZM9.69256 2.85712L10.1828 3.42467C10.9927 2.72507 12.25 3.3004 12.25 4.3706H13H13.75C13.75 2.01615 10.984 0.750421 9.20228 2.28956L9.69256 2.85712ZM13 16.3618H12.25C12.25 17.432 10.9927 18.0073 10.1828 17.3077L9.69256 17.8753L9.20228 18.4428C10.984 19.982 13.75 18.7163 13.75 16.3618H13ZM6.36317 5.73321L5.87289 5.16565C5.64583 5.3618 5.3558 5.46972 5.05576 5.46973L5.05576 6.21973L5.05577 6.96973C5.71587 6.96972 6.35394 6.73228 6.85346 6.30077L6.36317 5.73321Z"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="1"
                      />
                      <path
                        d="M17 3L2 18"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span className="btn-text">
                  {soundEnabled ? t("disableSound") : t("enableSound")}
                </span>
              </button>

              <button
                onClick={toggleFullscreen}
                className="actionbar-btn secondary"
                style={{ minHeight: "36px", padding: "10px 16px" }}
                aria-label={t("exitFullscreen")}
                title={t("exitFullscreen")}
              >
                <span className="btn-icon">
                  <Minimize2 size={16} />
                </span>
                <span className="btn-text">{t("exitFullscreen")}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs - Filters are now in TopNavbar when not in fullscreen mode */}
      {!isFullscreen && (
        <div className="mb-3">
          {/* Controls Row - Dropdown and Buttons */}
          <div className="controls-container mt-3">
            {/* Branch Dropdown - Full width on mobile, auto on larger screens */}
            <div
              className="dropdown-container mb-md-0"
              style={{ minWidth: "310px" }}
            >
              <CustomDropdown
                options={[
                  { _id: "all", name: t("allBranches", "All Branches") },
                  ...branches.map((branch) => ({
                    ...branch,
                    name:
                      branch.displayName ||
                      (isArabic
                        ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
                        : branch?.name?.en || branch?.name),
                  })),
                ]}
                selectedOption={
                  selectedBranch || { _id: "all", name: t("allBranches", "All Branches") }
                }
                handleSelect={(option) => {
                  if (option._id === "all") {
                    handleBranchSelect(null);
                  } else {
                    handleBranchSelect(option);
                  }
                }}
                placeholder={t("Select Branch")}
                label={t("branches")}
                showClear={false}
              />
            </div>

            {/* Buttons Row - Full width on mobile, auto on larger screens */}
            <div className="buttons-row d-flex justify-content-end gap-2">
              <div className="filter-group d-flex align-items-center gap-2" style={{ flex: "1 1 auto" }}>
                <div style={{ minWidth: "220px" }} className="platform-filter-block d-flex align-items-center gap-2">
                  <span className="filter-label" style={{ color: "#0d2d3c", fontWeight: 500, fontSize: 14 }}>{t("toolBar.platform")}</span>
                  <FilterDropdown
                    orderStatuses={platformOptions}
                    activeTab={selectedPlatform}
                    className="flex-item"
                    showLabel={false}
                    onSelectItem={(id) => {
                      setSelectedPlatform(id);
                    }}
                  />
                </div>
                <div style={{ minWidth: "200px" }} className="date-filter-block d-flex align-items-center gap-2">
                  <span className="filter-label" style={{ color: "#0d2d3c", fontWeight: 500, fontSize: 14 }}>{t("toolBar.date")}</span>
                  <FilterDropdown
                    orderStatuses={datePresetOptions}
                    activeTab={datePreset}
                    setActiveTab={(id) => setDatePreset(id)}
                    className="flex-item"
                    showLabel={false}
                    onSelectItem={(id) => {
                      setDatePreset(id);
                      if (id !== "customRange") {
                        setDateFrom("");
                        setDateTo("");
                      }
                    }}
                    shouldCloseOnSelect={(id) => id !== "customRange"}
                    renderExtraMenu={(close) => (
                      <div className="p-2">
                          <div className="mb-2">
                            <small className="text-muted">{t("toolBar.dateFrom")}</small>
                            <input
                              type="date"
                              value={dateFrom}
                              onChange={(e) => {
                                const val = e.target.value;
                                setDateFrom(val);
                                if (val && dateTo) {
                                  setDatePreset("customRange");
                                  applyDatePreset({ from: val || null, to: dateTo || null });
                                  close();
                                }
                              }}
                              onClick={(e) => {
                                if (e.currentTarget.showPicker) {
                                  e.currentTarget.showPicker();
                                }
                              }}
                              className="form-control form-control-sm"
                            />
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">{t("toolBar.dateTo")}</small>
                            <input
                              type="date"
                              value={dateTo}
                              onChange={(e) => {
                                const val = e.target.value;
                                setDateTo(val);
                                if (dateFrom && val) {
                                  setDatePreset("customRange");
                                  applyDatePreset({ from: dateFrom || null, to: val || null });
                                  close();
                                }
                              }}
                              onClick={(e) => {
                                if (e.currentTarget.showPicker) {
                                  e.currentTarget.showPicker();
                                }
                              }}
                              className="form-control form-control-sm"
                            />
                          </div>
                          <div>
                            <Button
                              color="secondary"
                              size="sm"
                              className="w-100"
                              onClick={() => {
                                setDateFrom("");
                                setDateTo("");
                                setDatePreset("today");
                                close();
                              }}
                            >
                              {t("cancel")}
                            </Button>
                          </div>
                        </div>
                    )}
                  />
                </div>
              </div>
              {/* Sound toggle - styled like Orders page */}
              <button
                className={`actionbar-btn ${
                  soundEnabled ? "sound-on" : "sound-off"
                }`}
                onClick={toggleSound}
                style={{ minHeight: "36px", padding: "8px 12px" }}
                aria-label={soundEnabled ? t("disableSound") : t("enableSound")}
                title={soundEnabled ? t("disableSound") : t("enableSound")}
              >
                <span className="btn-icon">
                  {soundEnabled ? (
                    <svg
                      className="oh-icon-directional"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path
                        d="M15 6.3706C15 4.65827 12.9884 3.73774 11.6926 4.85712L8.36317 7.73321C7.99988 8.04704 7.53583 8.21972 7.05576 8.21973L5.49998 8.21974C4.11928 8.21975 3 9.33903 3 10.7197V14.0127C3 15.3934 4.11929 16.5127 5.5 16.5127H7.0558C7.53587 16.5127 7.99993 16.6854 8.36322 16.9992L11.6926 19.8753C12.9884 20.9947 15 20.0741 15 18.3618V12.3662V6.3706Z"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      />
                      <path
                        d="M18 15.3672C18.6279 14.5315 19 13.4928 19 12.3672C19 11.2416 18.6279 10.2028 18 9.36719"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M20 18.3672C21.2558 16.6959 22 14.6184 22 12.3672C22 10.116 21.2558 8.03851 20 6.36719"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="oh-icon-directional"
                      width="16"
                      height="16"
                      viewBox="0 0 18 21"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path
                        d="M13.75 11C13.75 10.5858 13.4142 10.25 13 10.25C12.5858 10.25 12.25 10.5858 12.25 11H13H13.75ZM9.40001 17.6226L9.89029 17.055L9.8799 17.046L9.86918 17.0374L9.40001 17.6226ZM8.46917 15.9149C8.14601 15.6558 7.67399 15.7077 7.41487 16.0308C7.15575 16.354 7.20767 16.826 7.53083 17.0851L8 16.5L8.46917 15.9149ZM3.49998 6.21974L3.49998 5.46974L3.49998 6.21974ZM9.69256 17.8753L10.1828 17.3077L9.69256 17.8753ZM5.05576 6.21973L5.05576 5.46973L5.05576 6.21973ZM6.36317 5.73321L6.85346 6.30077L10.1828 3.42467L9.69256 2.85712L9.20228 2.28956L5.87289 5.16565L6.36317 5.73321ZM3.49998 6.21974L3.49999 6.96974L5.05577 6.96973L5.05576 6.21973L5.05576 5.46973L3.49998 5.46974L3.49998 6.21974ZM1 12.0127H1.75V8.71974H1H0.25V12.0127H1ZM5.80003 14.5127V13.7627H3.5V14.5127V15.2627H5.80003V14.5127ZM13 12.5H12.25V16.3618H13H13.75V12.5H13ZM13 4.3706H12.25V7H13H13.75V4.3706H13ZM13 11H12.25V12.5H13H13.75V11H13ZM9.69256 17.8753L10.1828 17.3077L9.89029 17.055L9.40001 17.6226L8.90972 18.1901L9.20228 18.4428L9.69256 17.8753ZM9.40001 17.6226L9.86918 17.0374L8.46917 15.9149L8 16.5L7.53083 17.0851L8.93084 18.2077L9.40001 17.6226ZM1 12.0127H0.25C0.25 13.8076 1.70507 15.2627 3.5 15.2627V14.5127V13.7627C2.5335 13.7627 1.75 12.9792 1.75 12.0127H1ZM3.49998 6.21974L3.49998 5.46974C1.70506 5.46975 0.25 6.92482 0.25 8.71974H1H1.75C1.75 7.75324 2.53349 6.96974 3.49999 6.96974L3.49998 6.21974ZM9.69256 2.85712L10.1828 3.42467C10.9927 2.72507 12.25 3.3004 12.25 4.3706H13H13.75C13.75 2.01615 10.984 0.750421 9.20228 2.28956L9.69256 2.85712ZM13 16.3618H12.25C12.25 17.432 10.9927 18.0073 10.1828 17.3077L9.69256 17.8753L9.20228 18.4428C10.984 19.982 13.75 18.7163 13.75 16.3618H13ZM6.36317 5.73321L5.87289 5.16565C5.64583 5.3618 5.3558 5.46972 5.05576 5.46973L5.05576 6.21973L5.05577 6.96973C5.71587 6.96972 6.35394 6.73228 6.85346 6.30077L6.36317 5.73321Z"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="1"
                      />
                      <path
                        d="M17 3L2 18"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span className="btn-text">
                  {soundEnabled ? t("disableSound") : t("enableSound")}
                </span>
              </button>
              <button
                onClick={toggleFullscreen}
                className="actionbar-btn secondary"
                style={{ minHeight: "36px", padding: "10px 16px" }}
                aria-label={
                  isFullscreen ? t("exitFullscreen") : t("fullscreen")
                }
                title={isFullscreen ? t("exitFullscreen") : t("fullscreen")}
              >
                <span className="btn-icon">
                  {isFullscreen ? (
                    <Minimize2 size={16} />
                  ) : (
                    <Maximize2 size={16} />
                  )}
                </span>
                <span className="btn-text">
                  {isFullscreen ? t("exitFullscreen") : t("fullscreen")}
                </span>
              </button>
            </div>
          </div>

          <style jsx>{`
            .controls-container {
              display: flex;
              flex-direction: column;
              gap: 12px;
            }

            .dropdown-container {
              width: 100%;
            }

            .buttons-row {
              width: 100%;
              align-items: center;
            }

            @media (min-width: 1145px) {
              .controls-container {
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
              }

              .dropdown-container {
                width: auto;
                margin-bottom: 0 !important;
              }

              .buttons-row {
                width: auto;
                align-items: center;
              }
            }
          `}</style>
        </div>
      )}

      {/* Audio element for notifications - always enabled */}
      <div className="d-flex justify-content-end mb-3">
        <audio ref={audioRef} src="/sounds/notification.mp3" preload="auto" />
      </div>

      <>
        {/* Show skeleton loader when filter is loading */}
        {filterLoading ? (
          <div className="mt-4">{renderSkeletonLoader()}</div>
        ) : (
          <>
            {/* Orders Grid */}
            {orders?.length > 0 ? (
              <>
                <div className="mt-4 orders-grid">
                  {orders.map((order, index) => {
                    const isLastOrder = orders.length === index + 1;
                    const isUpdating = updatingOrders.has(order._id);

                    return (
                      <div
                        ref={isLastOrder ? lastOrderRef : null}
                        data-order-id={order._id}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "10px",
                          display: "flex",
                          flexDirection: "column",
                          minWidth: "0",
                          transition: "all 0.3s ease",
                        }}
                        className={`bg-white order-card ${
                          isUpdating ? "order-updating" : ""
                        }`}
                        key={order._id}
                      >
                        <div
                          className={`d-flex text-white justify-content-between ${getStatusColor(
                            order.loops_status
                          )}`}
                          style={{
                            borderTopLeftRadius: "10px",
                            borderTopRightRadius: "10px",
                            borderBottomLeftRadius: "0px",
                            borderBottomRightRadius: "0px",
                            padding: "8px 20px",
                            minHeight: "52px",
                            alignItems: "center",
                          }}
                        >
                          <div
                            className="d-flex flex-wrap gap-2 flex-grow-1 align-items-center"
                            style={{ minWidth: 0, flexShrink: 1 }}
                          >
                            <div className="d-flex align-items-center flex-shrink-0">
                              <img
                                src={platformLogos[order.platform]?.src}
                                alt={platformLogos[order.platform]?.alt}
                                className={`img-fluid ${
                                  platformLogos[order.platform]?.className || ""
                                }`}
                                style={{
                                  borderRadius: "5px",
                                  maxHeight: "90px",
                                  maxWidth: "110px",
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                            <div style={{ minWidth: 0, flex: "1 1 100px" }}>
                              <p
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "bold",
                                  lineHeight: "1.2",
                                }}
                                className="m-0"
                              >
                                #{getExternalOrderId(order)}
                              </p>
                              <p
                                style={{ fontSize: "11px", lineHeight: "1" }}
                                className="m-0"
                              >
                                {formatDistanceStrict(
                                  order.createdAt,
                                  new Date(),
                                  {
                                    addSuffix: true,
                                    locale:
                                      i18n.language === "ar" ? ar : undefined,
                                  }
                                )}
                              </p>
                            </div>
                          </div>

                          <div
                            className="d-flex align-items-center justify-content-end"
                            style={{ flexShrink: 0 }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                borderRadius: "4px",
                                letterSpacing: "0.3px",
                                textAlign: "right",
                                whiteSpace: "nowrap",
                                lineHeight: "1.2",
                              }}
                            >
                              {order.loops_status === "PENDING" && t("PENDING")}
                              {order.loops_status === "ACCEPTED" &&
                                t("inProgress")}
                              {order.loops_status === "COMPLETED" &&
                                t("COMPLETED")}
                              {order.loops_status === "DELIVERED" &&
                                t("DELIVERED")}
                            </span>
                          </div>
                        </div>

                        {order.loops_status === "PENDING" || order.userGetMode ? (
                          <div style={{ padding: "8px 20px" }}>
                            <OrderTimer
                              createdAt={order.createdAt}
                              platform={order.platform}
                              status={order.loops_status}
                              userGetMode={order.userGetMode}
                            />
                          </div>
                        ) : null}

                        <hr className="m-0" />

                        <div
                          className="flex-grow-1 order-items-list"
                          style={{ paddingTop: "20px" }}
                        >
                          {order.products?.map((product, pIndex) => (
                            <div
                              key={pIndex}
                              style={{
                                padding: "0 20px",
                                marginBottom: "16px",
                              }}
                            >
                              <div className="d-flex gap-2">
                                <span className="m-0 text-black">
                                  {product.quantity}
                                </span>
                                <div>
                                  <p
                                    className="m-0 text-black"
                                    style={{
                                      fontSize: "16px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {isArabic
                                      ? product.loops_product_id?.name?.ar ||
                                        product.name
                                      : product.loops_product_id?.name?.en ||
                                        product.name}
                                  </p>

                                  {product.modifiers?.length > 0 && (
                                    <div
                                      style={{
                                        marginLeft: "3px",
                                        fontSize: "12px",
                                        fontWeight: "400",
                                        color: "#8E98A8",
                                      }}
                                    >
                                      {product.modifiers.flatMap(
                                        (modifier, mIndex) =>
                                          modifier.options.map(
                                            (opt, oIndex) => (
                                              <div
                                                key={`${mIndex}-${oIndex}`}
                                                className="d-block"
                                              >
                                                {isArabic
                                                  ? opt.nameAr
                                                  : opt.nameEn}
                                              </div>
                                            )
                                          )
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {order.payment_method && (
                          <>
                            <hr className="m-0" />
                            <div style={{ padding: "16px 20px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    fontSize: "14px",
                                    color: "black",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      borderRadius: "50%",
                                      backgroundColor: "#083b4c",
                                    }}
                                  ></span>
                                  <span style={{ fontWeight: "600" }}>
                                    {t("paymentMethod")}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#6c757d",
                                    fontWeight: "400",
                                  }}
                                >
                                  {t(order.payment_method)}
                                </span>
                              </div>
                            </div>
                            <hr className="m-0" />
                          </>
                        )}

                        <div className="oh-ods-customer-block">
                          <div className="oh-ods-customer-block__name">
                            {order.customer_name || t("N/A", "N/A")}
                          </div>
                          <div className="oh-ods-customer-block__phone">
                            <PhoneOutlinedIcon
                              size={14}
                              className="oh-ods-customer-block__phone-icon"
                            />
                            <span className="oh-ods-customer-block__phone-text">
                              {getOrderContactPhone(order) || t("N/A", "N/A")}
                            </span>
                          </div>
                          {getOrderAddressDisplay(order) ? (
                            <div className="oh-ods-customer-block__address">
                              <AddressReveal
                                address={getOrderAddressDisplay(order)}
                              />
                            </div>
                          ) : null}
                        </div>

                        <div
                          className="d-flex gap-2 mt-auto"
                          style={{ padding: "12px 20px 20px 20px" }}
                        >
                          
                          {order.loops_status &&
                            order.loops_status.toUpperCase().trim() ===
                              "PENDING" && (
                              <>
                                <div className="flex-fill">
                                  <DisabledControlTooltipWrap
                                    className="w-100"
                                    active={isDashboardFulfillmentLocked(order)}
                                    title={t(fulfillmentLockTooltipKey(order))}
                                  >
                                    <button
                                      className="w-100"
                                    onClick={() => {
                                      if (isDashboardFulfillmentLocked(order)) return;
                                      // Only handle non-expired orders (expired orders auto-disappear)
                                      if (
                                        !isOrderExpired(
                                          order.createdAt,
                                          order.platform
                                        )
                                      ) {
                                        handleAction(
                                          order.appId,
                                          order._id,
                                          "ACCEPTED"
                                        );
                                      }
                                    }}
                                    disabled={
                                      isUpdating ||
                                      isOrderExpired(
                                        order.createdAt,
                                        order.platform
                                      ) ||
                                      isDashboardFulfillmentLocked(order)
                                    }
                                    style={{
                                      color: "#3e9e85",
                                      backgroundColor: "#e5f2ef",
                                      border: "1px solid #3e9e85",
                                      padding: "6px 30px",
                                      borderRadius: "5px",
                                      fontSize: "0.85rem",
                                      fontWeight: "bold",
                                      cursor:
                                        isUpdating ||
                                        isDashboardFulfillmentLocked(order)
                                          ? "not-allowed"
                                          : "pointer",
                                      opacity:
                                        isUpdating ||
                                        isDashboardFulfillmentLocked(order)
                                          ? 0.7
                                          : 1,
                                      minHeight: "32px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {isUpdating ? "..." : t("accept")}
                                  </button>
                                </DisabledControlTooltipWrap>
                                </div>
                                <div className="flex-fill">
                                  <DisabledControlTooltipWrap
                                    className="w-100"
                                    active={isDashboardFulfillmentLocked(order)}
                                    title={t(fulfillmentLockTooltipKey(order))}
                                  >
                                    <button
                                      className="w-100"
                                    onClick={() => {
                                      if (isDashboardFulfillmentLocked(order)) return;
                                      if (!isProcessingReject) {
                                        // Only handle non-expired orders (expired orders auto-disappear)
                                        if (
                                          !isOrderExpired(
                                            order.createdAt,
                                            order.platform
                                          )
                                        ) {
                                          setOrderToDelete(order);
                                          setShowDeleteModal(true);
                                        }
                                      }
                                    }}
                                    disabled={
                                      isProcessingReject ||
                                      isOrderExpired(
                                        order.createdAt,
                                        order.platform
                                      ) ||
                                      isDashboardFulfillmentLocked(order)
                                    }
                                    style={{
                                      color: "#eb6969",
                                      backgroundColor: "#fdeded",
                                      border: "1px solid #eb6969",
                                      padding: "6px 30px",
                                      borderRadius: "5px",
                                      fontSize: "0.85rem",
                                      fontWeight: "bold",
                                      cursor:
                                        isProcessingReject ||
                                        isOrderExpired(
                                          order.createdAt,
                                          order.platform
                                        ) ||
                                        isDashboardFulfillmentLocked(order)
                                          ? "not-allowed"
                                          : "pointer",
                                      opacity:
                                        isProcessingReject ||
                                        isDashboardFulfillmentLocked(order)
                                          ? 0.7
                                          : 1,
                                      minHeight: "32px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {isProcessingReject &&
                                    orderToDelete?._id === order._id
                                      ? "..."
                                      : t("reject")}
                                  </button>
                                </DisabledControlTooltipWrap>
                                </div>
                              </>
                            )}
                          {order.loops_status &&
                            order.loops_status.toUpperCase().trim() ===
                              "ACCEPTED" && (
                              <>
                                <div
                                  className="flex-grow-1"
                                  style={{ minWidth: 0 }}
                                >
                                  <DisabledControlTooltipWrap
                                    className="w-100"
                                    active={isDashboardFulfillmentLocked(order)}
                                    title={t(fulfillmentLockTooltipKey(order))}
                                  >
                                    <button
                                      className="w-100 outline-btn"
                                    onClick={() => {
                                      if (isDashboardFulfillmentLocked(order)) return;
                                      handleAction(
                                        order.appId,
                                        order._id,
                                        order.userGetMode === "delivery"
                                          ? "READY FOR DELIVERY"
                                          : "READY FOR DELIVERY"
                                      );
                                    }}
                                    disabled={
                                      isUpdating ||
                                      isDashboardFulfillmentLocked(order)
                                    }
                                    style={{
                                      padding: "6px 30px",
                                      borderRadius: "5px",
                                      fontSize: "0.85rem",
                                      fontWeight: "bold",
                                      cursor:
                                        isUpdating ||
                                        isDashboardFulfillmentLocked(order)
                                          ? "not-allowed"
                                          : "pointer",
                                      opacity:
                                        isUpdating ||
                                        isDashboardFulfillmentLocked(order)
                                          ? 0.7
                                          : 1,
                                      minHeight: "32px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {isUpdating ? "..." : t("ready")}
                                  </button>
                                </DisabledControlTooltipWrap>
                                </div>
                                <button
                                  onClick={() => handlePrint(order)}
                                  style={{
                                    padding: "8px 12px",
                                    borderRadius: "5px",
                                    backgroundColor: "#f0a169ff",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  title={t("print")}
                                >
                                  <Printer size={18} color="#ffffff" />
                                </button>
                              </>
                            )}
                          {order.loops_status &&
                            (order.loops_status.toUpperCase().trim() ===
                              "COMPLETED" ||
                              order.loops_status.toUpperCase().trim() ===
                                "DELIVERED") && (
                              <button
                                onClick={() => handlePrint(order)}
                                style={{
                                  padding: "8px 12px",
                                  borderRadius: "5px",
                                  backgroundColor:
                                    order.loops_status.toUpperCase().trim() ===
                                    "COMPLETED"
                                      ? "#41a1c1"
                                      : "#33997f",
                                  border: "none",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginLeft: "auto",
                                }}
                                title={t("print")}
                              >
                                <Printer size={18} color="#ffffff" />
                              </button>
                            )}
                        </div>

                        <style>
                          {`.outline-btn {
                            background-color: transparent;
                            border: 1px solid #f0a169ff;
                            color: #f0a169ff;
                            transition: all 0.2s ease;
                          }
                          .outline-btn:hover:not(:disabled) {
                            background-color: #f0a169ff;
                            color: white;
                          }
                          .outline-btn:disabled {
                            opacity: 0.7;
                            cursor: not-allowed;
                          }`}
                        </style>
                      </div>
                    );
                  })}
                </div>

                {/* Loading indicator for pagination */}
                {loading && !filterLoading && (
                  <div className="text-center py-3">
                    <Skeleton width={200} height={20} />
                  </div>
                )}

                {/* No more orders message */}
                {!hasMore && orders.length > 0 && (
                  <div className="text-center py-4 text-muted">
                    {t("noMoreOrders") || "No more orders to load"}
                  </div>
                )}
              </>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <h3 className="mb-4">{t("No Orders Available")}</h3>
                {activeFilter !== "all" && (
                  <button
                    className="filter-btn orange"
                    onClick={() => handleFilterChange("all")}
                    style={{
                      padding: "0.5rem 1.5rem",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "transparent",
                      transition: "all 0.2s ease",
                      fontWeight: 500,
                    }}
                  >
                    {t("viewAllOrders") || "View All Orders"}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </>

      {/* Reject Confirmation Modal */}
      <DeleteModal
        text={
          orderToDelete
            ? t("rejectOrderConfirm", {
                id: getExternalOrderId(orderToDelete),
              })
            : t("Are you sure you want to process this order?")
        }
        showModal={showDeleteModal && orderToDelete !== null}
        isLoading={isProcessingReject}
        confirmText="reject"
        confirmIcon="fas fa-times"
        confirmDelete={async () => {
          if (orderToDelete && isDashboardFulfillmentLocked(orderToDelete)) {
            handleCloseModal();
            return;
          }
          if (orderToDelete && !isProcessingReject) {
            setIsProcessingReject(true);
            try {
              await handleAction(
                orderToDelete.appId,
                orderToDelete._id,
                "REJECTED"
              );
              handleCloseModal();
            } catch (error) {
              console.error("Error rejecting order:", error);
              setIsProcessingReject(false);
            }
          }
        }}
        onClose={handleCloseModal}
      />
    </section>
  );
};

export default OrdersDisplay;
